import { useState } from 'react';
import clsx from 'clsx';
import CaretIcon from '../../../../../../assets/Svg/CaretIcon';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import { useDeferredUnmount } from '../../../../../../common/hooks/useDeferredUnmount';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import MatchStrength from '../../../../../../common/MatchStrength/MatchStrength';
import { COLORS } from '../../../../../../utils/constants/colors';
import { ROW_MOTION_MS } from '../legalCasesConstants';
import { buildCaseDetailsSections, statusChip } from './caseDetailsSections';
import styles from './CaseDetailsDrawer.module.scss';
import { CASE_DETAILS_FETCH_FAILED_TEXT, EMPTY_VALUE, HEADER_CASE_LABEL } from './constants';
import { CaseDetailsDrawerProps, DetailSectionData } from './types';

const ROWS_KEY = 'rows';

const CaseDetailsSection = ({ title, rows, defaultExpanded }: DetailSectionData) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const { heldKeys, hold, release } = useDeferredUnmount<typeof ROWS_KEY>(ROW_MOTION_MS);
    const collapsing = heldKeys.includes(ROWS_KEY);

    const toggle = () => {
        if (expanded) hold(ROWS_KEY);
        else release(ROWS_KEY);
        setExpanded(!expanded);
    };

    return (
        <div className={styles.Section}>
            <button type="button" className={styles.SectionHeader} aria-expanded={expanded} onClick={toggle}>
                <span aria-hidden="true" className={clsx(styles.Chevron, { [styles.Chevron_Collapsed]: !expanded })}>
                    <CaretIcon colour={COLORS.DARK_GREY} width="13" height="8" />
                </span>
                <span className={styles.SectionTitle}>{title}</span>
            </button>
            {(expanded || collapsing) && (
                <div className={clsx(styles.SectionRowsWrap, { [styles.SectionRowsWrap_Collapsing]: collapsing })}>
                    <div className={styles.SectionRowsClip}>
                        <div className={styles.SectionRows}>
                            {rows.map((row) => (
                                <div className={clsx(styles.Row, { [styles.Row_GroupStart]: row.startsGroup })} key={row.label}>
                                    <span className={styles.LabelCell}>
                                        <span className={styles.Label}>{row.label}</span>
                                        {row.labelAction}
                                    </span>
                                    <span className={styles.Value}>{row.value || EMPTY_VALUE}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const CaseDetailsDrawer = ({
    open,
    onClose,
    caseDetails,
    onDocumentClick,
    onDownloadAllClick,
    isLoadingDetails,
    detailsFetchFailed,
}: CaseDetailsDrawerProps) => {
    if (!open || !caseDetails) return null;

    const body = isLoadingDetails ? (
        <div className={styles.LoadingState}>
            <LoadingSpinner color={COLORS.PRIMARY_PURPLE} height="1.5rem" />
        </div>
    ) : detailsFetchFailed ? (
        <div className={styles.ErrorState}>{CASE_DETAILS_FETCH_FAILED_TEXT}</div>
    ) : (
        <>
            {/* Above the case's own details: whether this case is the borrower's at all comes first. A case
                the engine never scored has nothing to show, so it gets no card rather than a "Not evaluated" one. */}
            {caseDetails.matchEvidence && <MatchStrength matchEvidence={caseDetails.matchEvidence} />}
            <div className={styles.SectionsCard}>
                {buildCaseDetailsSections(caseDetails, { onDocumentClick, onDownloadAllClick }).map((section) => (
                    <CaseDetailsSection key={section.title} {...section} />
                ))}
            </div>
        </>
    );

    return (
        <SideDrawer
            allowClose
            showHeading
            onClose={onClose}
            width={'50.25rem'}
            heading={
                <div className={styles.Heading}>
                    <span className={styles.HeadingLabel}>{HEADER_CASE_LABEL}</span>
                    <span aria-hidden="true" className={styles.HeadingDivider} />
                    <span className={styles.CaseNumber}>{caseDetails.casetypeCaseNoCaseYr}</span>
                    {statusChip(caseDetails.caseStatus)}
                </div>
            }
        >
            <div className={styles.Body}>{body}</div>
        </SideDrawer>
    );
};

export default CaseDetailsDrawer;
